"use client";

import type { GalleryComponent, GalleryImage } from "../../types";
import { useRendererRuntime } from "../../runtime/RendererRuntime";

/**
 * Gallery block — a responsive captioned image grid.
 *
 * Used by the migrated legacy "Gallérie des photos" pages and, where the
 * banknote image files exist in the legacy capture, the billets-de-banque
 * pages. Each image `src` is a bucket-relative storage key (e.g.
 * `images/galleries/apa_1.png`), not a browser-loadable URL — emitting it raw
 * into `<img src>` resolves it against the current page path and 404s.
 *
 * This is a **client component** so it can read the renderer runtime context
 * (`assetResolver`) — the same env-aware resolution `DataRecordPdfPreview`
 * applies to PDFs and `RichTextRenderer` applies to inline images. The
 * resolver turns each key into an environment-aware absolute URL (preview and
 * production), and passes already-absolute URLs through unchanged.
 *
 * New-site styling: navy `#121212` captions, white cards with `slate-200`
 * borders and the shared soft blue card shadow — matching the sibling
 * editorial renderers and the `InformationHub` card convention.
 */

/** Maps an explicit column count to a Tailwind grid-columns class. */
function columnsClass(columns?: number): string {
  switch (columns) {
    case 1:
      return "grid-cols-1";
    case 2:
      return "grid-cols-1 sm:grid-cols-2";
    case 4:
      return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";
    case 3:
    default:
      // Default responsive layout when no explicit count is given.
      return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
  }
}

/** Gallery renderer for migrated photo / banknote galleries. */
export function GalleryRenderer({ component }: { component: GalleryComponent }) {
  const { assetResolver } = useRendererRuntime();

  const images: GalleryImage[] = Array.isArray(component.content?.images)
    ? component.content.images
    : [];

  // Resolve every src through the asset resolver and drop images with no
  // usable src (empty key, or resolver returned "").
  const resolved = images
    .map((image) => {
      const key = (image?.src ?? "").trim();
      const src = key ? (assetResolver ? assetResolver(key) : key) : "";
      return { src, caption: image?.caption?.trim() ?? "" };
    })
    .filter((image) => image.src.length > 0);

  if (resolved.length === 0) return null;

  return (
    <section className="gallery-block">
      <div
        data-gallery-columns={component.content?.columns ?? ""}
        className={`grid gap-4 sm:gap-6 ${columnsClass(component.content?.columns)}`}
      >
        {resolved.map((image, index) => (
          <figure
            key={`${image.src}-${index}`}
            className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[var(--shadow-e1)]"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={image.src}
              alt={image.caption || `Photo ${index + 1}`}
              loading="lazy"
              className="h-56 w-full object-cover"
            />
            {image.caption && (
              <figcaption className="px-3 py-2 text-sm text-[#121212]">
                {image.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </section>
  );
}
